import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminProcurementPage = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddOrderModal, setShowAddOrderModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [draggedMaterial, setDraggedMaterial] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [sites, setSites] = useState([]);
  
  const [materialForm, setMaterialForm] = useState({
    name: '',
    quantity: '',
    unit: '',
    description: '',
    site_id: '',
    needed_date: '',
    notes: '',
    priority: 'normal',
    linked_tasks: []
  });

  // Task linking state
  const [linkableTasks, setLinkableTasks] = useState([]);
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Bulk actions state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [targetMoveSection, setTargetMoveSection] = useState('');

  // Archive filter state
  const [archivedPeriod, setArchivedPeriod] = useState('6_months');

  // Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'danger'
  });
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    title: '',
    message: ''
  });

  // Page Tips collapsed state
  const [isTipsCollapsed, setIsTipsCollapsed] = useState(true);

  // Fetch all materials for procurement
  const fetchMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const params = archivedPeriod ? { archived_period: archivedPeriod } : {};
      const response = await axios.get(`${API}/api/admin/procurement/materials`, { params });
      setMaterials(response.data || []);
    } catch (error) {
      console.error('Failed to fetch materials:', error);
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  }, [archivedPeriod]);

  // Fetch sites for dropdown
  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/sites`);
      setSites(response.data || []);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      setSites([]);
    }
  }, []);

  // Fetch linkable tasks for a site
  const fetchLinkableTasks = useCallback(async (siteId, searchQuery = '') => {
    if (!siteId) {
      setLinkableTasks([]);
      return;
    }
    
    try {
      setLoadingTasks(true);
      const params = searchQuery ? { search: searchQuery } : {};
      const response = await axios.get(`${API}/api/admin/sites/${siteId}/tasks/linkable`, { params });
      setLinkableTasks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch linkable tasks:', error);
      setLinkableTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }, []);

  // Handle site change - fetch linkable tasks
  const handleSiteChange = (siteId) => {
    setMaterialForm(prev => ({ ...prev, site_id: siteId, linked_tasks: [] }));
    setTaskSearchQuery('');
    fetchLinkableTasks(siteId);
  };

  // Handle task selection toggle
  const handleTaskToggle = (taskId) => {
    setMaterialForm(prev => {
      const currentTasks = prev.linked_tasks || [];
      if (currentTasks.includes(taskId)) {
        return { ...prev, linked_tasks: currentTasks.filter(id => id !== taskId) };
      } else {
        return { ...prev, linked_tasks: [...currentTasks, taskId] };
      }
    });
  };

  // Handle task search
  const handleTaskSearch = (query) => {
    setTaskSearchQuery(query);
    if (materialForm.site_id) {
      fetchLinkableTasks(materialForm.site_id, query);
    }
  };

  useEffect(() => {
    fetchMaterials();
    fetchSites();
  }, [fetchMaterials, fetchSites]);

  // Group materials by procurement status
  const groupedMaterials = {
    pending: materials.filter(m => m.procurement_status === 'pending'),
    placed: materials.filter(m => m.procurement_status === 'placed'),
    archived: materials.filter(m => m.procurement_status === 'archived')
  };

  // Handle material form submission
  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!materialForm.name || !materialForm.name.trim()) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please enter a material name.'
      });
      return;
    }
    
    if (!materialForm.quantity || !materialForm.quantity.trim()) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please enter a quantity.'
      });
      return;
    }
    
    if (!materialForm.site_id) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please select a site for this material request.'
      });
      return;
    }

    if (!materialForm.needed_date || !materialForm.needed_date.trim()) {
      setErrorModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please select a date when this material is needed.'
      });
      return;
    }
    
    
    try {
      if (editingMaterial) {
        // Update existing material
        await axios.put(`${API}/api/admin/procurement/materials/${editingMaterial.id}`, materialForm);
        
        setShowAddOrderModal(false);
        resetMaterialForm();
        
        setSuccessModal({
          isOpen: true,
          title: 'Material Updated',
          message: `"${materialForm.name}" has been successfully updated.`
        });
        
        fetchMaterials();
      } else {
        // Create new material
        await axios.post(`${API}/api/admin/procurement/materials`, {
          ...materialForm,
          requested_by: 'admin',
          procurement_status: 'pending'
        });
        
        setShowAddOrderModal(false);
        
        const materialName = materialForm.name;
        resetMaterialForm();
        
        setSuccessModal({
          isOpen: true,
          title: 'Material Created',
          message: `"${materialName}" has been successfully added to Pending Orders.`
        });
        
        fetchMaterials();
      }
    } catch (error) {
      console.error('Failed to save material:', error);
      setErrorModal({
        isOpen: true,
        title: 'Save Failed',
        message: 'Failed to save material. Please try again.'
      });
    }
  };

  const resetMaterialForm = () => {
    setMaterialForm({
      name: '',
      quantity: '',
      unit: '',
      description: '',
      site_id: '',
      needed_date: '',
      notes: '',
      priority: 'normal',
      linked_tasks: []
    });
    setEditingMaterial(null);
    setLinkableTasks([]);
    setTaskSearchQuery('');
  };

  // Handle material edit
  const handleEdit = (material) => {
    let resolvedSiteId = material.site_id || '';
    const matchingSite = sites.find(s => s.site_id === material.site_id || s.id === material.site_id);
    if (matchingSite) {
      resolvedSiteId = matchingSite.site_id;
    }
    
    setEditingMaterial(material);
    setMaterialForm({
      name: material.name || '',
      quantity: material.quantity || '',
      unit: material.unit || '',
      description: material.description || '',
      site_id: resolvedSiteId,
      needed_date: material.needed_date || '',
      notes: material.notes || '',
      priority: material.priority || 'normal',
      linked_tasks: material.linked_tasks || []
    });
    if (resolvedSiteId) {
      fetchLinkableTasks(resolvedSiteId);
    }
    setShowAddOrderModal(true);
  };

  // Handle material duplicate
  const handleDuplicate = (material) => {
    let resolvedSiteId = material.site_id || '';
    const matchingSite = sites.find(s => s.site_id === material.site_id || s.id === material.site_id);
    if (matchingSite) {
      resolvedSiteId = matchingSite.site_id;
    }
    
    setEditingMaterial(null);
    setMaterialForm({
      name: `${material.name || ''} (Copy)`,
      quantity: material.quantity || '',
      unit: material.unit || '',
      description: material.description || '',
      site_id: resolvedSiteId,
      needed_date: material.needed_date || '',
      notes: material.notes || '',
      priority: material.priority || 'normal',
      linked_tasks: material.linked_tasks || []
    });
    if (resolvedSiteId) {
      fetchLinkableTasks(resolvedSiteId);
    }
    setShowAddOrderModal(true);
  };

  // Handle material delete
  const handleDelete = async (material) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Material',
      message: `Are you sure you want to delete "${material.name}"? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/api/admin/procurement/materials/${material.id}`);
          setConfirmModal({ ...confirmModal, isOpen: false });
          setSuccessModal({
            isOpen: true,
            title: 'Material Deleted',
            message: `"${material.name}" has been successfully deleted.`
          });
          fetchMaterials();
        } catch (error) {
          console.error('Failed to delete material:', error);
          setConfirmModal({ ...confirmModal, isOpen: false });
          setErrorModal({
            isOpen: true,
            title: 'Delete Failed',
            message: 'Failed to delete material. Please try again.'
          });
        }
      }
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e, material) => {
    setDraggedMaterial(material);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    
    if (draggedMaterial && draggedMaterial.procurement_status !== targetStatus) {
      try {
        await axios.put(`${API}/api/admin/procurement/materials/${draggedMaterial.id}/status`, null, {
          params: { procurement_status: targetStatus }
        });
        fetchMaterials();
      } catch (error) {
        console.error('Failed to update material status:', error);
        setErrorModal({
          isOpen: true,
          title: 'Update Failed',
          message: 'Failed to update material status. Please try again.'
        });
      }
    }
    
    setDraggedMaterial(null);
  };

  // Bulk actions functions
  const handleItemSelect = (materialId, sectionStatus) => {
    if (selectedSection && selectedSection !== sectionStatus) {
      setSelectedItems([materialId]);
      setSelectedSection(sectionStatus);
      return;
    }

    if (selectedItems.includes(materialId)) {
      const newSelection = selectedItems.filter(id => id !== materialId);
      setSelectedItems(newSelection);
      if (newSelection.length === 0) {
        setSelectedSection(null);
      }
    } else {
      setSelectedItems([...selectedItems, materialId]);
      setSelectedSection(sectionStatus);
    }
  };

  const handleSelectAll = (sectionStatus) => {
    const sectionMaterials = groupedMaterials[sectionStatus];
    const sectionMaterialIds = sectionMaterials.map(m => m.id);

    if (selectedSection !== sectionStatus) {
      setSelectedItems(sectionMaterialIds);
      setSelectedSection(sectionStatus);
      return;
    }

    const allSelected = sectionMaterialIds.every(id => selectedItems.includes(id));
    if (allSelected) {
      setSelectedItems([]);
      setSelectedSection(null);
    } else {
      setSelectedItems(sectionMaterialIds);
      setSelectedSection(sectionStatus);
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectedSection(null);
  };

  const handleBulkMove = async () => {
    if (!targetMoveSection || selectedItems.length === 0) {
      return;
    }

    try {
      const movePromises = selectedItems.map(materialId =>
        axios.put(`${API}/api/admin/procurement/materials/${materialId}/status`, null, {
          params: { procurement_status: targetMoveSection }
        })
      );

      const results = await Promise.allSettled(movePromises);
      const successes = results.filter(result => result.status === 'fulfilled');
      const failures = results.filter(result => result.status === 'rejected');
      
      await fetchMaterials();
      clearSelection();
      setShowMoveModal(false);
      setTargetMoveSection('');
      
      if (failures.length === 0) {
        setSuccessModal({
          isOpen: true,
          title: 'Materials Moved',
          message: `Successfully moved ${successes.length} material(s) to ${targetMoveSection} orders.`
        });
      } else if (successes.length > 0) {
        setErrorModal({
          isOpen: true,
          title: 'Partial Success',
          message: `Moved ${successes.length} materials successfully, but ${failures.length} failed. Please try again for the failed items.`
        });
      } else {
        setErrorModal({
          isOpen: true,
          title: 'Move Failed',
          message: 'Failed to move materials. Please try again.'
        });
      }
      
    } catch (error) {
      console.error('Failed to move materials:', error);
      setErrorModal({
        isOpen: true,
        title: 'Move Failed',
        message: 'Failed to move materials. Please try again.'
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Delete Materials',
      message: `Are you sure you want to delete ${selectedItems.length} selected material(s)? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          const deletePromises = selectedItems.map(materialId =>
            axios.delete(`${API}/api/admin/procurement/materials/${materialId}`)
          );

          const results = await Promise.allSettled(deletePromises);
          const successes = results.filter(result => result.status === 'fulfilled');
          const failures = results.filter(result => result.status === 'rejected');
          
          await fetchMaterials();
          clearSelection();
          setConfirmModal({ ...confirmModal, isOpen: false });
          
          if (failures.length === 0) {
            setSuccessModal({
              isOpen: true,
              title: 'Materials Deleted',
              message: `Successfully deleted ${successes.length} material(s).`
            });
          } else if (successes.length > 0) {
            setErrorModal({
              isOpen: true,
              title: 'Partial Success',
              message: `Deleted ${successes.length} materials successfully, but ${failures.length} failed. Please try again for the failed items.`
            });
          } else {
            setErrorModal({
              isOpen: true,
              title: 'Delete Failed',
              message: 'Failed to delete materials. Please try again.'
            });
          }
          
        } catch (error) {
          console.error('Failed to delete materials:', error);
          setConfirmModal({ ...confirmModal, isOpen: false });
          setErrorModal({
            isOpen: true,
            title: 'Delete Failed',
            message: 'Failed to delete materials. Please try again.'
          });
        }
      }
    });
  };

  const getAvailableMoveSections = () => {
    const sections = [
      { value: 'pending', label: 'Pending Orders' },
      { value: 'placed', label: 'Placed Orders' },
      { value: 'archived', label: 'Archived Orders' }
    ];
    return sections.filter(section => section.value !== selectedSection);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Render materials table with dark theme
  const renderMaterialsTable = (materialsData, status, title) => {
    const isCurrentSection = selectedSection === status;
    const sectionMaterialIds = materialsData.map(m => m.id);
    const selectedInSection = selectedItems.filter(id => sectionMaterialIds.includes(id));
    const allSectionSelected = sectionMaterialIds.length > 0 && sectionMaterialIds.every(id => selectedItems.includes(id));
    const someSectionSelected = selectedInSection.length > 0 && !allSectionSelected;

    return (
      <div
        className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden"
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, status)}
      >
        <div className="flex items-center justify-between p-4 bg-slate-700 border-b border-slate-600">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            {title} ({materialsData.length} items)
            {isCurrentSection && selectedInSection.length > 0 && (
              <span className="ml-2 text-sm text-purple-400 font-medium">
                ({selectedInSection.length} selected)
              </span>
            )}
          </h3>
          
          {/* Dropdown filter for archived orders only */}
          {status === 'archived' && (
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-400">Show orders from:</label>
              <select
                value={archivedPeriod}
                onChange={(e) => setArchivedPeriod(e.target.value)}
                className="px-3 py-2 text-sm bg-slate-600 border border-slate-500 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="1_week">1 Week</option>
                <option value="1_month">1 Month</option>
                <option value="3_months">3 Months</option>
                <option value="6_months">6 Months</option>
              </select>
            </div>
          )}
        </div>
        
        <div className="p-6 pt-4">
        
        {materialsData.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-12 h-12 text-gray-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <p className="text-sm">No {title.toLowerCase()} found</p>
            <p className="text-xs text-gray-600">Drag materials here to change their status</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
            <table className="w-full table-fixed border-collapse">
              <thead className="sticky top-0 bg-slate-700 z-10">
                <tr className="border-b border-slate-600">
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-12 bg-slate-700">
                    <input
                      type="checkbox"
                      checked={allSectionSelected}
                      ref={input => {
                        if (input) input.indeterminate = someSectionSelected;
                      }}
                      onChange={() => handleSelectAll(status)}
                      className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500 focus:ring-2"
                    />
                  </th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-48 bg-slate-700">Material Name</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-20 bg-slate-700">Quantity</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-16 bg-slate-700">Unit</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-32 bg-slate-700">Site</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-36 bg-slate-700">Linked Tasks</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-28 bg-slate-700">Date Needed</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-20 bg-slate-700">Priority</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-64 bg-slate-700">Description</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-28 bg-slate-700">Request Date</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-32 bg-slate-700">Requested By</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-400 text-sm w-24 bg-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {materialsData.map((material) => (
                  <tr
                    key={material.id}
                    className={`border-b border-slate-700/50 hover:bg-slate-700/50 cursor-move transition-colors ${
                      selectedItems.includes(material.id) ? 'bg-purple-500/10' : ''
                    }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, material)}
                  >
                    <td className="py-3 px-2 w-12">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(material.id)}
                        onChange={() => handleItemSelect(material.id, status)}
                        className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500 focus:ring-2"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="py-3 px-2 w-48">
                      <div className="font-medium text-white text-sm break-words whitespace-normal">{material.name}</div>
                    </td>
                    <td className="py-3 px-2 w-20">
                      <div className="text-gray-300 text-sm break-words whitespace-normal">{material.quantity}</div>
                    </td>
                    <td className="py-3 px-2 w-16">
                      <div className="text-gray-300 text-sm break-words whitespace-normal">{material.unit}</div>
                    </td>
                    <td className="py-3 px-2 w-32">
                      <div className="text-gray-300 text-sm break-words whitespace-normal">{material.site_name || 'No Site'}</div>
                    </td>
                    <td className="py-3 px-2 w-36">
                      {material.linked_task_details && material.linked_task_details.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {material.linked_task_details.slice(0, 2).map(task => (
                            <span
                              key={task.id}
                              className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded-full ${
                                task.status === 'in_progress' 
                                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                                  : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                              }`}
                              title={`${task.title} (${task.status})`}
                            >
                              {task.title.length > 15 ? `${task.title.substring(0, 15)}...` : task.title}
                            </span>
                          ))}
                          {material.linked_task_details.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{material.linked_task_details.length - 2} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-xs">No tasks linked</span>
                      )}
                    </td>
                    <td className="py-3 px-2 w-28">
                      <div className="text-gray-300 text-sm break-words whitespace-normal">
                        {material.needed_date ? new Date(material.needed_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </div>
                    </td>
                    <td className="py-3 px-2 w-20">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        material.priority === 'urgent' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                        material.priority === 'low' ? 'bg-slate-600 text-gray-300 border border-slate-500' :
                        'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                      }`}>
                        {material.priority || 'normal'}
                      </span>
                    </td>
                    <td className="py-3 px-2 w-64">
                      <div className="text-gray-300 text-sm break-words whitespace-normal">
                        {material.description}
                        {material.notes && (
                          <div className="text-gray-500 italic text-xs mt-1">Note: {material.notes}</div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 w-28">
                      <div className="text-gray-300 text-sm break-words whitespace-normal">{formatDate(material.request_date)}</div>
                    </td>
                    <td className="py-3 px-2 w-32">
                      <div className="text-gray-300 text-sm break-words whitespace-normal">{material.requested_by_name || material.requested_by}</div>
                    </td>
                    <td className="py-3 px-2 w-24">
                      <div className="flex space-x-1">
                        {status === 'pending' && (
                          <button
                            onClick={() => handleEdit(material)}
                            className="text-blue-400 hover:text-blue-300 p-1 transition-colors"
                            title="Edit material"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleDuplicate(material)}
                          className="text-green-400 hover:text-green-300 p-1 transition-colors"
                          title="Duplicate material"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(material)}
                          className="text-red-400 hover:text-red-300 p-1 transition-colors"
                          title="Delete material"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-slate-900 -m-8 p-8">
        {/* Modern Header - Purple Gradient */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-1">Materials</h1>
              <p className="text-purple-200">Track and manage material orders across all projects</p>
            </div>
            <button
              onClick={() => {
                resetMaterialForm();
                setShowAddOrderModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-400 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Add New Order</span>
            </button>
          </div>
        </div>

        {/* Page Tips - Collapsible */}
        <div className="mb-6 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <button
            onClick={() => setIsTipsCollapsed(!isTipsCollapsed)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/50 transition-colors duration-200"
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium text-white">Page Tips</span>
            </div>
            <svg 
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isTipsCollapsed ? '' : 'rotate-180'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {!isTipsCollapsed && (
            <div className="px-4 pb-4">
              <ul className="text-xs text-gray-400 space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span><span className="text-purple-300 font-medium">Drag and drop</span> materials between the Pending, Placed, and Archived sections to change their status</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span>Use the checkboxes to select multiple materials for bulk actions (move or delete)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span>Link materials to tasks to track which jobs require specific materials</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span>Set priority levels to highlight urgent material requests</span>
                </li>
              </ul>
            </div>
          )}
        </div>
        
        <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 p-6">
          {/* Bulk Actions Section */}
          {selectedItems.length > 0 && (
            <div className="mb-6 bg-purple-500/10 rounded-xl p-4 border border-purple-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div className="text-lg font-semibold text-white">
                    Bulk Actions ({selectedItems.length} items selected)
                  </div>
                  <div className="ml-2 text-sm text-purple-400 font-medium capitalize">
                    from {selectedSection} orders
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowMoveModal(true)}
                    className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-indigo-800 transition-all duration-200 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Move
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="bg-red-500/20 text-red-300 border border-red-500/30 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-all duration-200 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Delete
                  </button>
                  <button
                    onClick={clearSelection}
                    className="text-gray-400 hover:text-white px-3 py-2 rounded-lg border border-slate-600 hover:bg-slate-700 transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {renderMaterialsTable(groupedMaterials.pending, 'pending', 'Pending Orders')}
              {renderMaterialsTable(groupedMaterials.placed, 'placed', 'Placed Orders')}
              {renderMaterialsTable(groupedMaterials.archived, 'archived', 'Archived Orders')}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Order Modal - Dark Theme */}
      {showAddOrderModal && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => {
            setShowAddOrderModal(false);
            setEditingMaterial(null);
          }}
        >
          <div 
            className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[95vh] flex flex-col border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">
                {editingMaterial ? 'Edit Order' : 'Add New Order'}
              </h3>
              <button
                onClick={() => {
                  setShowAddOrderModal(false);
                  setEditingMaterial(null);
                }}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleMaterialSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Material Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={materialForm.name}
                    onChange={(e) => setMaterialForm({...materialForm, name: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Steel Rebar, Concrete Mix"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={materialForm.quantity}
                    onChange={(e) => setMaterialForm({...materialForm, quantity: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., 10, 15-20"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Unit</label>
                  <input
                    type="text"
                    value={materialForm.unit}
                    onChange={(e) => setMaterialForm({...materialForm, unit: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., kg, pieces, meters, tons"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Site <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={materialForm.site_id}
                    onChange={(e) => handleSiteChange(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="" className="bg-slate-800">Select a site...</option>
                    {sites.map(site => (
                      <option key={site.site_id} value={site.site_id} className="bg-slate-800">
                        {site.site_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Task Linking Section */}
              {materialForm.site_id && (
                <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Link to Tasks <span className="text-gray-500">(Optional)</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Select tasks that require this material. Only pending and in-progress tasks are shown.
                  </p>
                  
                  <div className="relative mb-3">
                    <input
                      type="text"
                      value={taskSearchQuery}
                      onChange={(e) => handleTaskSearch(e.target.value)}
                      placeholder="Search tasks..."
                      className="w-full px-3 py-2 pl-9 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                    <svg className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>

                  <div className="max-h-48 overflow-y-auto border border-slate-600 rounded-lg bg-slate-700">
                    {loadingTasks ? (
                      <div className="p-4 text-center text-gray-500 text-sm">Loading tasks...</div>
                    ) : linkableTasks.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">No pending or in-progress tasks found for this site.</div>
                    ) : (
                      linkableTasks.map(task => (
                        <label
                          key={task.id}
                          className={`flex items-start p-3 cursor-pointer hover:bg-slate-600 border-b border-slate-600 last:border-b-0 ${
                            materialForm.linked_tasks?.includes(task.id) ? 'bg-purple-500/10' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={materialForm.linked_tasks?.includes(task.id) || false}
                            onChange={() => handleTaskToggle(task.id)}
                            className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 bg-slate-700 border-slate-500 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-white">{task.title}</span>
                              <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                                task.status === 'in_progress' 
                                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                                  : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                              }`}>
                                {task.status === 'in_progress' ? 'In Progress' : 'Pending'}
                              </span>
                            </div>
                            {task.assigned_user_names && task.assigned_user_names.length > 0 && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                Assigned: {task.assigned_user_names.join(', ')}
                              </p>
                            )}
                          </div>
                        </label>
                      ))
                    )}
                  </div>

                  {materialForm.linked_tasks && materialForm.linked_tasks.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-400 mb-2">
                        Selected Tasks ({materialForm.linked_tasks.length}):
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {materialForm.linked_tasks.map(taskId => {
                          const task = linkableTasks.find(t => t.id === taskId);
                          return task ? (
                            <span
                              key={taskId}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded-full border border-purple-500/30"
                            >
                              {task.title.length > 25 ? `${task.title.substring(0, 25)}...` : task.title}
                              <button
                                type="button"
                                onClick={() => handleTaskToggle(taskId)}
                                className="hover:text-purple-100"
                              >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description / Product Link</label>
                <textarea
                  value={materialForm.description}
                  onChange={(e) => setMaterialForm({...materialForm, description: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  placeholder="Additional details, specifications, or product link..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Date Needed <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={materialForm.needed_date}
                    onChange={(e) => setMaterialForm({...materialForm, needed_date: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                  <select
                    value={materialForm.priority}
                    onChange={(e) => setMaterialForm({...materialForm, priority: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="low" className="bg-slate-800">Low</option>
                    <option value="normal" className="bg-slate-800">Normal</option>
                    <option value="urgent" className="bg-slate-800">Urgent</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes / Special Requirements</label>
                <textarea
                  value={materialForm.notes}
                  onChange={(e) => setMaterialForm({...materialForm, notes: e.target.value})}
                  rows="2"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  placeholder="Any special delivery instructions or requirements..."
                />
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-4 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddOrderModal(false);
                    setEditingMaterial(null);
                  }}
                  className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-700 text-white rounded-lg hover:from-purple-700 hover:to-indigo-800 transition-all duration-200"
                >
                  {editingMaterial ? 'Update Order' : 'Create Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Move Modal - Dark Theme */}
      {showMoveModal && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => {
            setShowMoveModal(false);
            setTargetMoveSection('');
          }}
        >
          <div 
            className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">Move Materials</h3>
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setTargetMoveSection('');
                }}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-300 mb-2">
                  Move <span className="font-semibold text-purple-400">{selectedItems.length} selected material(s)</span> from{' '}
                  <span className="font-semibold capitalize">{selectedSection} Orders</span> to:
                </p>
              </div>

              <div className="space-y-3">
                {getAvailableMoveSections().map((section) => (
                  <label key={section.value} className="flex items-center p-3 border border-slate-600 rounded-lg hover:bg-slate-700 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="moveSection"
                      value={section.value}
                      checked={targetMoveSection === section.value}
                      onChange={(e) => setTargetMoveSection(e.target.value)}
                      className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="ml-3 text-white font-medium">{section.label}</span>
                  </label>
                ))}
              </div>

              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-700 mt-6">
                <button
                  onClick={() => {
                    setShowMoveModal(false);
                    setTargetMoveSection('');
                  }}
                  className="px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkMove}
                  disabled={!targetMoveSection}
                  className={`px-6 py-2 rounded-lg transition-all duration-200 ${
                    targetMoveSection
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-700 text-white hover:from-purple-700 hover:to-indigo-800'
                      : 'bg-slate-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Move Materials
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ ...successModal, isOpen: false })}
        title={successModal.title}
        message={successModal.message}
      />

      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </>
  );
};

export default AdminProcurementPage;
